import { useAccount as useEVMAccount, useChainId } from "wagmi";
import { getConnectedWallet } from "./getConnectedWallet";
import { useEffect, useState } from "react";
import { useAccount as useSolanaAccount } from "./solana/useAccount";
import { glideLocalStorage } from "./glideLocalStorage";
import { useGlideDepositConfig } from "./useDepositConfig";
import memStore from "./memStore";

export function useConnectedWallet() {
  const chainId = useChainId();
  const depositConfig = useGlideDepositConfig();
  const { address: evmAddress, isConnected: isEVMConnected } = useEVMAccount();
  const { address: solanaAddress, isConnected: isSolanaConnected } =
    useSolanaAccount();

  const [useExternalWallet, setUseExternalWallet] = useState(
    localStorage.getItem("glide.useExternalWallet") === "true",
  );

  const [useParentWallet, setUseParentWallet] = useState(
    localStorage.getItem("glide.useParentWallet") === "true",
  );

  const [externalWalletEcosystem, setExternalWalletEcosystem] = useState<
    "evm" | "solana" | undefined
  >(
    localStorage.getItem("glide.externalWalletEcosystem") === "solana"
      ? "solana"
      : localStorage.getItem("glide.externalWalletEcosystem") === "evm"
        ? "evm"
        : undefined,
  );

  const hasParentAppWallet = Boolean(
    new URLSearchParams(window.location.search).get("connectedWalletAddress") ||
      memStore.getItem("connectedWalletAddress"),
  );

  const [connectedWallet, setConnectedWallet] = useState<{
    isConnected: boolean;
    isReady: boolean;
    canDisconnect: boolean;
    hasParentAppWallet: boolean;
    address?: string;
  }>({
    isConnected: false,
    isReady: false,
    canDisconnect: false,
    hasParentAppWallet,
    address: undefined,
  });

  useEffect(() => {
    // Run immediately on mount
    const updateWallet = async () => {
      const newWallet = await getConnectedWallet({
        mode: depositConfig.mode,
        wagmiAccount: {
          chainId,
          address: evmAddress,
          isConnected: isEVMConnected,
        },
        solanaAccount: {
          address: solanaAddress,
          isConnected: isSolanaConnected,
        },
        useExternalWallet,
        externalWalletEcosystem,
      });
      setConnectedWallet(newWallet);
    };
    updateWallet();

    // Then set up interval for continuous updates
    const interval = setInterval(async () => {
      const newUseExternalWallet =
        (await glideLocalStorage.getItem("glide.useExternalWallet")) === "true";
      const newUseParentWallet =
        (await glideLocalStorage.getItem("glide.useParentWallet")) === "true";
      const newExternalWalletEcosystem =
        (await glideLocalStorage.getItem("glide.externalWalletEcosystem")) ===
        "solana"
          ? "solana"
          : (await glideLocalStorage.getItem(
                "glide.externalWalletEcosystem",
              )) === "evm"
            ? "evm"
            : undefined;

      setUseExternalWallet(newUseExternalWallet);
      setUseParentWallet(newUseParentWallet);
      setExternalWalletEcosystem(newExternalWalletEcosystem);

      const newWallet = await getConnectedWallet({
        mode: depositConfig.mode,
        wagmiAccount: {
          chainId,
          address: evmAddress,
          isConnected: isEVMConnected,
        },
        solanaAccount: {
          address: solanaAddress,
          isConnected: isSolanaConnected,
        },
        useExternalWallet: newUseExternalWallet,
        externalWalletEcosystem: newExternalWalletEcosystem,
      });

      setConnectedWallet(newWallet);
    }, 300);

    return () => {
      clearInterval(interval);
    };
  }, [
    chainId,
    depositConfig.mode,
    evmAddress,
    externalWalletEcosystem,
    isEVMConnected,
    isSolanaConnected,
    solanaAddress,
    useExternalWallet,
    useParentWallet,
  ]);

  return connectedWallet;
}
